import { Component, OnInit, ViewChild } from "@angular/core";
import { ClassificationSystem } from "../Model/classification/ClassificationSystem";
import { TranslateService } from "@ngx-translate/core";
import { ClassificationService } from "../Services/classification.service";
import { LoginService } from "../Services/login.service";
import { SystemService } from "../Services/system.service";
import { confirm } from "devextreme/ui/dialog";
import { ClassificationClass } from "../Model/classification/ClassificationClass";
import { StartAction } from "../Model/Dto/StartAction";
import { JobService } from "../Services/job.service";
import { OAuthService } from "angular-oauth2-oidc";
import { DxFileUploaderComponent } from "devextreme-angular";

@Component({
    selector: "editClassificationSystem",
    templateUrl: "editClassificationSystem.component.html",
    standalone: false
})
export class EditClassificationSystemComponent implements OnInit {
    @ViewChild(DxFileUploaderComponent)
    dxfileuploader: DxFileUploaderComponent;

    classificationSystems: ClassificationSystem[];
    newClassificationSystem: ClassificationSystem;
    createClassificationSystemPopupVisible: boolean;
    selectedClassificationSystem: ClassificationSystem;
    selectedClassificationClass: ClassificationClass;
    classificationClasses: ClassificationClass[];
    loadingClassificationClasses: boolean = false;
    createDuplicate: boolean = false;
    edcSelected: boolean = false;
    showClassificationFileUpload: boolean = false;
    fileTypes: string;
    uploadHeaders: any;

    constructor(public classificationService: ClassificationService, private translate: TranslateService,
                public loginService: LoginService, private systemService: SystemService, private jobService: JobService,
                private oauthService: OAuthService) {
        this.systemService.currentNavigationTitle = this.translate.instant('Klassifizierung');
        this.getClassificationSystemDisplayText = this.getClassificationSystemDisplayText.bind(this);
        this.createClassificationSystem = this.createClassificationSystem.bind(this);
        this.createCatalogWithClassification = this.createCatalogWithClassification.bind(this);
        this.refreshClassificationSystem = this.refreshClassificationSystem.bind(this);
        this.fileTypes = ".zip";
        this.uploadHeaders = {
            CustomerId: this.loginService.currentUser.customerId
        };
    }

    async ngOnInit(): Promise<void> {
        await this.loadClassificationSystems();
    }

    onClassificationSystemChanged(e: any) {
        this.edcSelected = this.selectedClassificationSystem.requiredAddon == "EDC";
    }

    async fillFeaturesInClassificationClass(classificationClass: ClassificationClass) {
        if (classificationClass) {
            classificationClass.features = await this.classificationService.getClassificationClassFeatures(classificationClass.id);
        }
        this.selectedClassificationClass = classificationClass;
    }

    showCreateClassificationSystemPopup() {
        this.newClassificationSystem = new ClassificationSystem();
        this.createDuplicate = false;
        this.createClassificationSystemPopupVisible = true;
    }

    async createClassificationSystem() {
        this.createClassificationSystemPopupVisible = false;
        if (this.createDuplicate) {
            await this.duplicateClassificationSystem();
            return;
        }

        this.newClassificationSystem.id = undefined;

        this.newClassificationSystem.createdByCustomerId = this.loginService.currentUser.customerId;
        let createdClassificationSystem = await this.classificationService.createClassificationSystem(this.newClassificationSystem);

        this.classificationSystems.push(createdClassificationSystem);
        this.selectedClassificationSystem = createdClassificationSystem;
    }

    async duplicateClassificationSystem() {
        this.newClassificationSystem.createdByCustomerId = this.loginService.currentUser.customerId;

        let createdClassificationSystem = await this.classificationService.duplicateClassificationSystem(this.newClassificationSystem);

        this.classificationSystems.push(createdClassificationSystem);
        this.selectedClassificationSystem = createdClassificationSystem;
    }


    refreshClassificationSystem() {
        this.showClassificationFileUpload = true;
    }

    onBeforeSendUpload(e: { request: XMLHttpRequest }) {
        e.request.withCredentials = true;
        e.request.setRequestHeader('Authorization', 'Bearer ' + this.oauthService.getAccessToken());
    }

    onUploaded() {
        this.dxfileuploader.instance.reset();
        this.showClassificationFileUpload = false;
        this.systemService.notifyInfo(this.translate.instant("Job zur Aktualisierung der EDC Klassifizierung gestartet"));
    }

    showEditClassificationSystem() {
        if (this.selectedClassificationSystem == undefined) {
            this.systemService.notifyInfo(this.translate.instant("Bitte Klassifizierungs-System auswählen"));
            return;
        }
        this.selectedClassificationClass = undefined;
    }

    showDuplicateClassificationSystemPopup() {
        if (this.selectedClassificationSystem == undefined) {
            this.systemService.notifyInfo(this.translate.instant("Bitte Klassifizierungs-System auswählen"));
            return;
        }
        this.newClassificationSystem = JSON.parse(JSON.stringify(this.selectedClassificationSystem));
        this.newClassificationSystem.name += this.translate.instant(" - Duplikat");
        this.newClassificationSystem.isGlobal = false;
        this.createDuplicate = true;
        this.createClassificationSystemPopupVisible = true;
    }

    async deleteClassificationSystem() {
        if (this.selectedClassificationSystem == undefined) {
            this.systemService.notifyInfo(this.translate.instant("Bitte Klassifizierungs-System auswählen"));
            return;
        }
        let dialogResult = await confirm(this.translate.instant("ConfirmDeleteClassificationSystem"), this.translate.instant("Klassifizierungs-System löschen"));
        if (!dialogResult) {
            return;
        }

        this.classificationService.deleteClassificationSystem(this.selectedClassificationSystem).then(() => {
            var index = this.classificationSystems.indexOf(this.selectedClassificationSystem);
            if (index > -1) {
                this.classificationSystems.splice(index, 1);
                this.resetClassificationSystemSelectBox();
            }
        }).catch(() => {
            this.systemService.notifyWarning(this.translate.instant("Fehler beim Löschen, Daten werden neu geladen"));
            this.loadClassificationSystems();
        });
    }

    resetClassificationSystemSelectBox() {
        this.selectedClassificationSystem = undefined;
    }

    async loadClassificationSystems() {
        this.selectedClassificationClass = undefined;
        this.selectedClassificationSystem = undefined;

        this.classificationSystems = await this.classificationService.getClassificationSystemsForEditor();
    }

    getClassificationSystemDisplayText(classificationSystem: ClassificationSystem) {
        if (classificationSystem == null) {
            return "";
        }
        var displayText = classificationSystem.name;
        if (classificationSystem.isGlobal) {
            if (this.loginService.isAdmin) {
                displayText += " (Global)";
            } else {
                displayText += " (Read-Only)";
            }
        }
        return displayText;
    }

    createCatalogWithClassification() {
        if (this.selectedClassificationSystem == undefined) {
            this.systemService.notifyInfo(this.translate.instant("Bitte Klassifizierungs-System auswählen"));
            return;
        }

        let startAction = new StartAction();
        startAction.customerId = this.loginService.currentUser.customerId;
        startAction.actionId = `CreateClassificationCatalog(${this.selectedClassificationSystem.id})`;

        this.jobService.startJob(startAction)
            .subscribe(next => {
                this.systemService.notifyInfo(this.translate.instant("Der Katalog wird per Job erzeugt"));
            });
    }
}
